import { useMutation, useQuery } from "@tanstack/react-query";
import { ArrowLeft, BadgeCheck, CalendarDays, Coins, IdCard } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";

import { AuditTraceViewer } from "../components/AuditTraceViewer";
import { Badge } from "../components/Badge";
import { ErrorPanel, Loading } from "../components/Loading";
import { api, ApiError } from "../lib/api";
import { formatMoney } from "../lib/format";
import { localizedName, useLocale } from "../lib/i18n";
import type {
  EmployeeDetail,
  LeaveBalanceResponse,
  LoanEligibilityResponse,
} from "../lib/types";

function EmployeeHeader({ employee }: { employee: EmployeeDetail }) {
  const { t, locale } = useLocale();
  const { t: tt } = useTranslation();
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6">
      <div className="flex items-start justify-between gap-6">
        <div>
          <p className="font-mono text-xs text-slate-500">{employee.employee_code}</p>
          <h1 className="mt-1 text-2xl font-bold">{localizedName(employee, locale)}</h1>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <Badge tone={employee.employment_status === "active" ? "green" : "amber"}>
              {employee.employment_status}
            </Badge>
            <Badge tone="slate">{employee.role_category}</Badge>
            {employee.is_saudi_national ? (
              <Badge tone="green">{tt("employee.badge_sa_national")}</Badge>
            ) : (
              <Badge tone="sky">
                {tt("employee.badge_non_sa_prefix")} · {employee.nationality}
              </Badge>
            )}
          </div>
        </div>
        {employee.current_contract ? (
          <div className="rounded-lg bg-slate-50 px-4 py-3 text-end">
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">
              {t.detail_total_wage}
            </p>
            <p className="mt-1 font-mono text-xl font-bold tabular-nums text-slate-900">
              {formatMoney(employee.current_contract.total_wage)}
            </p>
            <p className="mt-1 text-xs text-slate-500">
              {employee.current_contract.contract_type} · {employee.current_contract.pay_frequency}
            </p>
          </div>
        ) : null}
      </div>

      <div className="mt-6 grid grid-cols-1 gap-3 text-sm md:grid-cols-3">
        <div className="flex items-center gap-2 text-slate-600">
          <IdCard className="h-4 w-4 text-slate-400" />
          <span className="font-mono text-xs">{employee.national_id_or_iqama}</span>
        </div>
        <div className="flex items-center gap-2 text-slate-600">
          <CalendarDays className="h-4 w-4 text-slate-400" />
          <span>
            {t.detail_hire_date}: <span className="tabular-nums">{employee.hire_date}</span>
          </span>
        </div>
        {employee.first_gosi_contribution_date ? (
          <div className="flex items-center gap-2 text-slate-600">
            <BadgeCheck className="h-4 w-4 text-slate-400" />
            <span>
              {tt("employee.gosi_from")}{" "}
              <span className="tabular-nums">{employee.first_gosi_contribution_date}</span>
            </span>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function LeaveBalanceSection({ employeeId }: { employeeId: string }) {
  const { t } = useLocale();
  const { t: tt } = useTranslation();
  const [resolutionDate, setResolutionDate] = useState("");

  const balanceQ = useQuery<LeaveBalanceResponse, ApiError>({
    queryKey: ["leave-balance", employeeId, resolutionDate],
    queryFn: () => api.getLeaveBalance(employeeId, resolutionDate || undefined),
  });

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <h2 className="text-lg font-semibold">{t.leave_section}</h2>
        <label className="flex items-center gap-2 text-xs text-slate-600">
          <span>{t.leave_resolution_date}</span>
          <input
            type="date"
            value={resolutionDate}
            onChange={(e) => setResolutionDate(e.target.value)}
            className="rounded border border-slate-200 px-2 py-1 font-mono text-xs"
          />
        </label>
      </div>

      {balanceQ.isPending ? (
        <div className="mt-4">
          <Loading />
        </div>
      ) : balanceQ.isError ? (
        <div className="mt-4">
          <ErrorPanel message={balanceQ.error.message} />
        </div>
      ) : (
        <>
          <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
            <StatCard
              label={t.leave_entitlement}
              value={String(balanceQ.data.entitlement_days)}
              unit={tt("employee.days_unit")}
            />
            <StatCard
              label={t.leave_taken}
              value={balanceQ.data.taken_days_this_year}
              unit={tt("employee.days_unit")}
            />
            <StatCard
              label={t.leave_remaining}
              value={balanceQ.data.remaining_days}
              unit={tt("employee.days_unit")}
              tone="emerald"
            />
          </div>
          <div className="mt-6">
            <AuditTraceViewer trace={balanceQ.data.audit_trace} />
          </div>
        </>
      )}
    </section>
  );
}

function LoanEligibilitySection({ employeeId }: { employeeId: string }) {
  const { t } = useLocale();
  const [resolutionDate, setResolutionDate] = useState("");
  const [multiplier, setMultiplier] = useState("");
  const [requiredTenure, setRequiredTenure] = useState("");

  const checkM = useMutation<LoanEligibilityResponse, ApiError>({
    mutationFn: () =>
      api.postLoanEligibility(employeeId, {
        resolution_date: resolutionDate || undefined,
        max_loan_multiplier: multiplier || undefined,
        required_tenure_months: requiredTenure ? Number(requiredTenure) : undefined,
      }),
  });

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-6">
      <h2 className="text-lg font-semibold">{t.loan_section}</h2>
      <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-3">
        <FieldInput
          label={t.leave_resolution_date}
          type="date"
          value={resolutionDate}
          onChange={setResolutionDate}
        />
        <FieldInput
          label={t.loan_multiplier}
          type="number"
          placeholder="6"
          value={multiplier}
          onChange={setMultiplier}
        />
        <FieldInput
          label={t.loan_required_tenure}
          type="number"
          placeholder="6"
          value={requiredTenure}
          onChange={setRequiredTenure}
        />
      </div>
      <div className="mt-4 flex justify-end">
        <button
          type="button"
          onClick={() => checkM.mutate()}
          disabled={checkM.isPending}
          className="inline-flex items-center gap-2 rounded-lg bg-brand px-4 py-2 text-sm font-medium text-brand-fg transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Coins className="h-4 w-4" />
          {t.loan_check}
        </button>
      </div>

      {checkM.isError ? (
        <div className="mt-4">
          <ErrorPanel message={checkM.error.message} />
        </div>
      ) : null}
      {checkM.data ? (
        <>
          <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-4">
            <StatCard label={t.loan_total_wage} value={formatMoney(checkM.data.total_wage)} />
            <StatCard
              label={t.loan_existing_balance}
              value={formatMoney(checkM.data.existing_loan_balance)}
            />
            <StatCard
              label={t.loan_tenure_eligible}
              value={checkM.data.is_tenure_eligible ? t.yes : t.no}
              tone={checkM.data.is_tenure_eligible ? "emerald" : "rose"}
            />
            <StatCard
              label={t.loan_max_eligible}
              value={formatMoney(checkM.data.max_eligible_amount)}
              tone="emerald"
            />
          </div>
          {checkM.data.rejection_reason ? (
            <p className="mt-3 rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-700">
              {checkM.data.rejection_reason}
            </p>
          ) : null}
          <div className="mt-6">
            <AuditTraceViewer trace={checkM.data.audit_trace} />
          </div>
        </>
      ) : null}
    </section>
  );
}

function StatCard({
  label,
  value,
  unit,
  tone = "slate",
}: {
  label: string;
  value: string;
  unit?: string;
  tone?: "slate" | "emerald" | "rose";
}) {
  const toneClass =
    tone === "emerald"
      ? "text-emerald-700"
      : tone === "rose"
        ? "text-rose-700"
        : "text-slate-900";
  return (
    <div className="rounded-lg bg-slate-50 px-4 py-3">
      <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">{label}</p>
      <p className={`mt-1 text-2xl font-bold tabular-nums ${toneClass}`}>{value}</p>
      {unit ? <p className="text-xs text-slate-500">{unit}</p> : null}
    </div>
  );
}

function FieldInput({
  label,
  type,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  type: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold uppercase tracking-widest text-slate-500">
        {label}
      </span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1 block w-full rounded-lg border border-slate-200 px-3 py-2 font-mono text-sm shadow-sm focus:border-slate-500 focus:outline-none"
      />
    </label>
  );
}

export function EmployeeDetailPage() {
  const { t } = useLocale();
  const { t: tt } = useTranslation();
  const { employeeId } = useParams<{ employeeId: string }>();

  const employeeQ = useQuery({
    queryKey: ["employee", employeeId],
    queryFn: () => api.getEmployee(employeeId!),
    enabled: Boolean(employeeId),
  });

  if (!employeeId) return <ErrorPanel message={tt("employee.missing_employee_id")} />;

  return (
    <div className="space-y-6">
      <Link
        to="/"
        className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-slate-900"
      >
        <ArrowLeft className="h-4 w-4" />
        {t.back_to_list}
      </Link>

      {employeeQ.isPending ? (
        <Loading />
      ) : employeeQ.isError ? (
        <ErrorPanel message={(employeeQ.error as ApiError).message} />
      ) : (
        <>
          <EmployeeHeader employee={employeeQ.data} />
          <LeaveBalanceSection employeeId={employeeId} />
          <LoanEligibilitySection employeeId={employeeId} />
        </>
      )}
    </div>
  );
}
